import { useEffect, useState } from "react";
import axios from "axios";

const AUTH_TOKEN_KEY = "enfarm_admin_access_token";
const AUTH_USER_KEY = "enfarm_admin_user";
const GOOGLE_OAUTH_STATE_KEY = "enfarm_google_oauth_state";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://api-ma.aiottech.site";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim();

const getGoogleRedirectUri = () => `${window.location.origin}/login`;

const authApi = axios.create({
  baseURL: API_BASE_URL,
});

let currentUserCache = null;
let currentUserPromise = null;
let currentUserHydratedFromApi = false;
let unauthorizedRedirectStarted = false;
const authListeners = new Set();

const toCompatUser = (user) => {
  if (!user) return null;

  return {
    ...user,
    displayName: user.userfullname || user.name || user.email,
    photoURL: user.picture,
  };
};

const getStoredUser = () => {
  const rawUser = localStorage.getItem(AUTH_USER_KEY);
  if (!rawUser) return null;

  try {
    return toCompatUser(JSON.parse(rawUser));
  } catch (error) {
    localStorage.removeItem(AUTH_USER_KEY);
    return null;
  }
};

currentUserCache = getStoredUser();

const notifyAuthListeners = () => {
  authListeners.forEach((listener) => listener(currentUserCache));
  window.dispatchEvent(new Event("auth-change"));
};

const subscribeAuth = (listener) => {
  authListeners.add(listener);
  return () => authListeners.delete(listener);
};

const getAuthPayload = (payload) => {
  if (!payload) return {};
  if (typeof payload !== "object") return {};
  if (payload.access_token) return payload;
  if (payload.data?.access_token) return payload.data;
  if (payload.content?.access_token) return payload.content;
  if (payload.token) return { ...payload, access_token: payload.token };
  if (payload.accessToken) return { ...payload, access_token: payload.accessToken };
  if (payload.data?.token) return { ...payload.data, access_token: payload.data.token };
  if (payload.data?.accessToken) return { ...payload.data, access_token: payload.data.accessToken };
  if (payload.content?.token) return { ...payload.content, access_token: payload.content.token };
  if (payload.content?.accessToken) return { ...payload.content, access_token: payload.content.accessToken };
  return payload;
};

const setSession = (payload) => {
  const { access_token, user } = getAuthPayload(payload);
  if (!access_token || access_token === "undefined" || access_token === "null") {
    clearSession();
    throw new Error("Login response is missing access_token. Check REACT_APP_API_BASE_URL.");
  }

  unauthorizedRedirectStarted = false;
  localStorage.setItem(AUTH_TOKEN_KEY, access_token);
  currentUserCache = toCompatUser(user);
  currentUserPromise = null;
  currentUserHydratedFromApi = Boolean(user);
  if (user) {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(AUTH_USER_KEY);
  }
  notifyAuthListeners();
};

const clearSession = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  currentUserCache = null;
  currentUserPromise = null;
  currentUserHydratedFromApi = false;
  notifyAuthListeners();
};

export const handleUnauthorized = () => {
  clearSession();
  if (unauthorizedRedirectStarted || window.location.pathname === "/login") return;
  unauthorizedRedirectStarted = true;
  window.location.replace("/login");
};

export const getAccessToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (!token || token === "undefined" || token === "null") {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return null;
  }
  return token;
};

export const getAuthorizationHeader = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getUserName = (user) => {
  if (!user?.email) return "";
  return user.email.split("@")[0];
};

export const hasRole = (user, role) => {
  return Array.isArray(user?.roles) && user.roles.includes(role);
};

export const hasPermission = (user, permission) => {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
};

export const hasAnyPermission = (user, permissions = []) => {
  if (hasRole(user, "super_admin")) return true;
  return permissions.some((permission) => hasPermission(user, permission));
};

export const exchangeGoogleCode = async (code) => {
  const response = await authApi.get("/auth/google", {
    params: {
      code,
      redirect_uri: getGoogleRedirectUri(),
    },
  });

  setSession(response.data);
  return toCompatUser(getAuthPayload(response.data).user);
};

export const fetchCurrentUser = async ({ force = false } = {}) => {
  const token = getAccessToken();
  if (!token) return null;
  if (currentUserCache && currentUserHydratedFromApi && !force) return currentUserCache;
  if (currentUserPromise && !force) return currentUserPromise;

  currentUserPromise = authApi
    .get("/auth/me", {
      headers: getAuthorizationHeader(),
    })
    .then((response) => {
      const user = response.data?.data || response.data?.content || response.data;
      currentUserCache = toCompatUser(user);
      currentUserHydratedFromApi = true;
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      notifyAuthListeners();
      return currentUserCache;
    })
    .catch((error) => {
      clearSession();
      throw error;
    })
    .finally(() => {
      currentUserPromise = null;
    });

  return currentUserPromise;
};

export const signInWithGoogle = () => {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error("Missing REACT_APP_GOOGLE_CLIENT_ID");
  }

  const redirectUri = getGoogleRedirectUri();
  const state = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  sessionStorage.setItem(GOOGLE_OAUTH_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid email profile",
    access_type: "offline",
    prompt: "consent",
    state,
  });

  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
};

export const validateGoogleAuthState = (state) => {
  const expectedState = sessionStorage.getItem(GOOGLE_OAUTH_STATE_KEY);
  sessionStorage.removeItem(GOOGLE_OAUTH_STATE_KEY);
  return Boolean(state && expectedState && state === expectedState);
};

export const logout = () => {
  clearSession();
};

export const useAuth = () => {
  const [user, setUser] = useState(() => currentUserCache);
  const [loading, setLoading] = useState(() => Boolean(getAccessToken() && !currentUserCache));
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const syncUser = async () => {
      const token = getAccessToken();
      if (!token) {
        if (mounted) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        if (!currentUserCache) setLoading(true);
        const currentUser = await fetchCurrentUser();
        if (mounted) {
          setUser(currentUser);
          setError(null);
        }
      } catch (err) {
        clearSession();
        if (mounted) {
          setUser(null);
          setError(err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    const unsubscribe = subscribeAuth((currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    syncUser();

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return [user, loading, error];
};
